import { Intro, DataTable, Pill, PopOverMenu } from 'components/commons';
import { useApi, useFilter, useMount } from 'hooks';
import useModal from 'hooks/useModal';
import React, { useCallback, useContext, useMemo } from 'react';
import { formatAmount, formatCurrency, formatName, prettifyCustomerStatus } from 'utils';
import { customerAccountColumns } from './customer-account-columns';
import CustomerFilter from './customer-account-filter';
import { customerAccountFilterState } from './customer-account-filter.state';
import CustomerForm from '../customer/customer-form';
import styles from './customer-account.module.scss';
import locale from 'localization';
import CustomerStatus from 'enums/driver-status';
import { getCustomerAccounts, getCustomerById } from 'apis/customer.api';
import CustomerAccountStatusModal from './customer-account-status-modal';
import CustomerAccountForm from '../customer/customer-account-form';
import CustomerAccountClaimsModal from './customer-account-claims-modal';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { DateTime, Path } from 'enums';
import moment from 'moment';
import { UserContext } from 'contexts';

const CustomerAccountModule = () => {
  const user = useContext(UserContext);

  const history = useHistory();
  const statusModal = useModal();
  const claimsModal = useModal();

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('customerId');
  const cifNumber = queryParams.get('cifNumber');
  const name = queryParams.get('name');

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(customerAccountFilterState());
  const customerFormModal = useModal();
  const customerAccountFormModal = useModal();

  const {
    request: getCustomerRequest,
    loading: fetchingCustomer,
    result: getCustomerResult = { customerAccounts: [], count: 0 }
  } = useApi({
    api: getCustomerAccounts,
    pageError: true,
    params: {
      customerId
    }
  });

  const {
    request: getCustomerDetailRequest,
    loading: fetchingCustomerDetail,
    result: getCustomerDetailResult = { customer: {} }
  } = useApi({
    api: getCustomerById,
    pageError: true,
    params: {
      customerId
    }
  });

  useMount(() => {
    fetchCustomers(requestState);
    fetchCustomerDetail();
  });

  const fetchCustomers = useCallback(
    (rs) => {
      getCustomerRequest(submitFilter({ ...rs }));
    },
    [getCustomerRequest, submitFilter]
  );

  const fetchCustomerDetail = useCallback(
    (rs) => {
      getCustomerDetailRequest(submitFilter({ ...rs }));
    },
    [getCustomerDetailRequest, submitFilter]
  );

  const changeStatusCb = useCallback(
    (customerAccountId, customerName, status, customerId, productId) => {
      statusModal.show({ status, customerAccountId, customerName, customerId, productId });
    },
    [statusModal]
  );

  const onViewClaimsCb = useCallback(
    (customerAccountId) => {
      claimsModal.show({ customerAccountId });
    },
    [claimsModal]
  );

  const preparedDriversData = useMemo(() => {
    const { customerAccounts } = getCustomerResult;
    if (customerAccounts.length > 0) {
      const preparedData = customerAccounts.map((c) => {
        const map = new Map();
        const customerName = formatName(c?.customer?.firstName || '', c?.customer?.lastName || '');
        map.set('customerAccountNumber', c.customerAccountNumber);
        map.set('name', customerName);
        map.set('cifNumber', c?.customer?.cifNumber || '--');
        map.set('balance', formatCurrency(c?.balance || 0));
        map.set(
          'product',
          <>
            {c?.product?.name || '--'}
            <br />
            <b>
              <div className={styles.subBusinessId}>
                {' '}
                {c.product?.name === 'Shared Capital' && `${formatAmount(c?.committedAmount)}`}
              </div>
            </b>
          </>
        );
        map.set('shares', c?.shares || '--');
        map.set('contribution', c?.contribution || '--');
        map.set(
          'status',
          <Pill
            grass={c.status === CustomerStatus.Active}
            deepRed={c.status === CustomerStatus.Terminated}
            cement={c.status === CustomerStatus.Delinquent}
          >
            {prettifyCustomerStatus(c.status)}
          </Pill>
        );
        let options = [];
        let deactivateUser;

        options.push({
          content: 'View Transactions',
          onClick: () => {
            history.push({
              pathname: Path.TransactionByProduct(c.product.type),
              search: `?customerAccountNumber=${c.customerAccountNumber}`
            });
          }
        });

        if (!c?.product?.name?.includes('Savings')) {
          options.push({
            content: 'View Claims',
            onClick: () => {
              onViewClaimsCb(c.customerAccountId);
            }
          });
        }

        if (['super-admin', 'admin'].includes(user?.role)) {
          deactivateUser = {
            content: c.status === CustomerStatus.Active ? locale.terminate : locale.activate,
            onClick: () => {
              changeStatusCb(c.customerAccountId, customerName, c.status, c.customerId, c.productId);
            }
          };
          options.push(deactivateUser);
        }

        map.set('options', <PopOverMenu options={options} />);
        return map;
      });
      return preparedData;
    }
    return [];
  }, [getCustomerResult, user?.role, history, onViewClaimsCb, changeStatusCb]);

  const onStatusChangeCb = useCallback(
    (status) => {
      const { requestState } = modifyFilters({ status, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onProductChangeCb = useCallback(
    (product) => {
      const { requestState } = modifyFilters({ product, productId: product.value, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onSearchCb = useCallback(
    (searchKey) => {
      const { requestState } = modifyFilters({ searchKey, page: 1, customerId });
      fetchCustomers(requestState);
    },
    [customerId, fetchCustomers, modifyFilters]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  // const onPrintPassbookCb = async () => {
  //   //   const { customerAccounts } = getCustomerResult;
  //   //   if (!customerAccounts.length) return;
  //   //   const printWindow = window.open('', '_blank');
  //   //   if (!printWindow) return;
  //   //   const htmlContent = `
  //   //   <html>
  //   //   <head>
  //   //     <title>Passbook</title>
  //   //     <style>
  //   //       body { font-family: Arial, sans-serif; padding: 20px; }
  //   //       h2 { text-align: center; }
  //   //       table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  //   //       th, td { border: 1px solid #000; padding: 8px; text-align: left; }
  //   //       th { background-color: #f2f2f2; }
  //   //     </style>
  //   //   </head>
  //   //   <body>
  //   //     <h2>Passbook Summary</h2>
  //   //     <table>
  //   //       <thead>
  //   //         <tr>
  //   //           <th>Account Number</th>
  //   //           <th>Product</th>
  //   //           <th>Balance</th>
  //   //           <th>Status</th>
  //   //         </tr>
  //   //       </thead>
  //   //       <tbody>
  //   //         ${customerAccounts
  //   //           .map(
  //   //             (account) => `
  //   //             <tr>
  //   //               <td>${account.customerAccountNumber}</td>
  //   //               <td>${account.product?.name || '--'}</td>
  //   //               <td>${formatCurrency(account.balance || 0)}</td>
  //   //               <td>${prettifyCustomerStatus(account.status)}</td>
  //   //             </tr>
  //   //           `
  //   //           )
  //   //           .join('')}
  //   //       </tbody>
  //   //     </table>
  //   //     <script>
  //   //       window.onload = function() {
  //   //         setTimeout(() => {
  //   //           window.print();
  //   //           window.onafterprint = () => window.close();
  //   //         }, 500);
  //   //       }
  //   //     </script>
  //   //   </body>
  //   //   </html>
  //   // `;
  //   //   printWindow.document.write(htmlContent);
  //   //   printWindow.document.close();
  // };

  const onViewDetailCb = () => {
    const { customer } = getCustomerDetailResult;
    if (!fetchingCustomerDetail && customer) {
      const c = customer || {};

      customerFormModal.show({
        title: "Member's Information",
        actionText: locale.save,
        create: false,
        customerId: c.customerId,
        initialState: {
          cifNumber: {
            disabled: true,
            value: c.cifNumber
          },
          firstName: {
            value: c.firstName
          },
          lastName: {
            value: c.lastName
          },
          middleName: {
            value: c.middleName
          },
          gender: {
            value: c.gender
          },
          civilStatus: {
            value: c.civilStatus
          },
          religion: {
            value: c.religion
          },
          mobileNumber: {
            value: c.mobileNumber
          },
          emailAddress: {
            value: c.emailAddress
          },
          placeOfBirth: {
            value: c.placeOfBirth
          },
          dateOfBirth: {
            value: c.dateOfBirth ? moment(c.dateOfBirth).format(DateTime.S) : ''
          },
          address: {
            value: c.address
          },
          sssNo: {
            value: c.sssNo
          },
          pagibigNo: {
            value: c.pagibigNo
          },
          philhealthNo: {
            value: c.philhealthNo
          },
          tinNo: {
            value: c.tinNo
          },
          motherName: {
            value: c.motherName
          },
          fatherName: {
            value: c.fatherName
          },
          work: {
            value: c.work
          },
          educationalAttainment: {
            value: c.educationalAttainment
          },
          spouseName: {
            value: c.spouseName
          },
          spouseWork: {
            value: c.spouseWork
          },
          emergencyContactNumber: {
            value: c.emergencyContactNumber
          },
          emergencyContactRelationship: {
            value: c.emergencyContactRelationship
          },
          emergencyContactAddress: {
            value: c.emergencyContactAddress
          },
          emergencyContactName: {
            value: c.emergencyContactName
          },
          type: {
            value: c.type
          },
          status: {
            value: c.status
          },
          bodApprovalDate: {
            value: c.bodApprovalDate ? moment(c.bodApprovalDate).format(DateTime.S) : ''
          },
          membershipId: {
            value: c.membershipId
          },
          boardResolutionNumber: {
            value: c.boardResolutionNumber
          }
        }
      });
    }
  };

  return (
    <div>
      <CustomerForm
        {...customerFormModal}
        refreshPage={() => {
          fetchCustomerDetail();
        }}
      />
      <CustomerAccountForm
        {...customerAccountFormModal}
        refreshPage={() => {
          fetchCustomers(requestState);
        }}
      />
      <div className={styles.intro}>
        <Intro
          title={cifNumber ? `${cifNumber} - ${name}` : 'Member Accounts'}
          subtitle={locale.manageYourCustomerAccounts}
          actionSecondaryText={'Back to Member List'}
          actionSecondaryOnClick={() => {
            history.push(Path.MemberList);
          }}
          actionText={'Add Customer Account'}
          actionOnClick={() => {
            customerAccountFormModal.show({
              title: 'Add Member Account',
              actionText: locale.save,
              create: true,
              customerId: Number(customerId) || '',
              initialState: {
                cifNumber: {
                  value: cifNumber || ''
                },
                productId: {
                  value: ''
                }
              }
            });
          }}
          action
        />
      </div>
      <div className={styles.filters}>
        <div>
          <CustomerFilter
            filterState={filterState}
            onStatusChange={onStatusChangeCb}
            onProductChange={onProductChangeCb}
            onSearchChange={modifyFilter}
            onSearch={onSearchCb}
            // onPrintPassbook={onPrintPassbookCb}
            onViewDetail={onViewDetailCb}
          />
        </div>
      </div>
      <div className={styles.table}>
        <DataTable
          loading={fetchingCustomer}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={customerAccountColumns}
          data={preparedDriversData}
          dataCount={getCustomerResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
        />
      </div>

      <CustomerAccountStatusModal refetchCustomers={() => fetchCustomers(requestState)} {...statusModal} />
      <CustomerAccountClaimsModal refetchCustomers={() => fetchCustomers(requestState)} {...claimsModal} />
    </div>
  );
};

export default CustomerAccountModule;
